import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass,
    faSpinner,
    faRotate,
    faPlus,
    faEdit,
    faTrash,
    faBroadcastTower,
    faImage,
    faVideo,
    faMicrophone,
    faCalendarDays,
    faBuilding,
    faTags,
    faCloudArrowUp,
    faCheck,
    faTimes,
    faExclamationTriangle,
    faFilter,
    faDownload,
    faCheckCircle,
    faTimesCircle,
    faExpand,
    faClock,
    faHeadphones
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './MediaCentreManagement.css';
import { getChoiceFieldOptions, getMediaCentreItems, sendToMediaCentre } from '../../services/mediaService';
import { galleryService } from '../../services/galleryService';
import EnhancedGalleryBrowser from '../Galleries/EnhancedGalleryBrowser';
import ModalSendToMediaCentre from '../../modals/ModalSendToMediaCentre';
import ModalAddMediaCentre from '../../modals/ModalAddMediaCentre';
import ModalBulkPublishToMediaCentre from '../../modals/ModalBulkPublishToMediaCentre';
import WolfoxWidget from '../Wolfox/WolfoxWidget';
import apiHelper from '../../utils/apiHelper';
import { useAudio } from '../../contexts/AudioContext';

// YouTube thumbnail fetcher (from member portal)
const getYouTubeThumbnail = (youtubeUrl) => {
    try {
        const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
        if (!videoId) {
            return null;
        }
        
        // Use maxresdefault for best quality, fallback to hqdefault
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        return thumbnailUrl;
    } catch (err) {

        return null;
    }
};

// Vimeo thumbnail fetcher (from member portal)
const getVimeoThumbnail = async (vimeoUrl) => {
    try {
        const videoId = vimeoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
        if (!videoId) {
            return null;
        }
        
        // Method 1: Try oEmbed API (modern approach)
        try {
            const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}&width=640`;
            const oembedResponse = await fetch(oembedUrl);
            
            if (!oembedResponse.ok) {
                if (oembedResponse.status === 404) {
                    const videoId = vimeoUrl.match(/vimeo\.com\/(\d+)/)?.[1];

                }
                throw new Error(`oEmbed API returned ${oembedResponse.status}`);
            }
            
            const oembedData = await oembedResponse.json();
            
            if (oembedData.thumbnail_url) {
                return oembedData.thumbnail_url;
            }
        } catch (oembedError) {

            // Continue to CDN fallback instead of deprecated v2 API
        }
        
        // Method 2: Try direct CDN approach
        try {
            const cdnUrl = `https://i.vimeocdn.com/video/${videoId}_640x360.jpg`;
            
            // Test if the URL actually works
            const testResponse = await fetch(cdnUrl, { method: 'HEAD' });
            if (testResponse.ok) {
                return cdnUrl;
            } 
        } catch (cdnError) {

        }
        
        return null;
        
    } catch (err) {

        return null;
    }
};

// Video thumbnail component that loads blob thumbnails
const VideoThumbnail = ({ thumbnailField, item }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadThumbnail = async () => {
            try {
                const apiUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${thumbnailField}`;
                const headers = await apiHelper.getHeaders('graph/getMediaContent');
                const response = await fetch(apiUrl, { headers, mode: 'cors' });

                if (!response.ok) {
                    throw new Error(`Failed to fetch thumbnail: ${response.status}`);
                }

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                setThumbnailUrl(url);
            } catch (err) {

                setError(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadThumbnail();

        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailField]);

    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa'
            }}>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (error || !thumbnailUrl) {
        // Show placeholder if thumbnail fails to load
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <FontAwesomeIcon icon={faVideo} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px', textAlign: 'center', padding: '0 8px' }}>
                    {item.topic || item.Topic || 'Video'}
                </span>
            </div>
        );
    }

    return (
        <>
            <img 
                src={thumbnailUrl}
                alt={item.topic || item.Topic || 'Video thumbnail'}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />

        </>
    );
};

// External video thumbnail component for Vimeo/YouTube
const ExternalVideoThumbnail = ({ sourceUrl, item }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadExternalThumbnail = async () => {
            try {
                let thumbnail = null;
                
                if (sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be')) {
                    thumbnail = getYouTubeThumbnail(sourceUrl);
                } else if (sourceUrl.includes('vimeo.com')) {
                    thumbnail = await getVimeoThumbnail(sourceUrl);
                }
                
                if (thumbnail) {
                    setThumbnailUrl(thumbnail);
                } else {
                    setError(true);
                }
            } catch (err) {

                setError(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadExternalThumbnail();
    }, [sourceUrl]);

    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa'
            }}>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (error || !thumbnailUrl) {
        // Show placeholder if external thumbnail fails to load
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <FontAwesomeIcon icon={faVideo} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px', textAlign: 'center', padding: '0 8px' }}>
                    {item.topic || item.Topic || 'Video'}
                </span>
            </div>
        );
    }

    return (
        <>
            <img 
                src={thumbnailUrl}
                alt={item.topic || item.Topic || 'Video thumbnail'}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={() => setError(true)}
            />

        </>
    );
};

// Audio thumbnail component that loads blob thumbnails
const AudioThumbnail = ({ thumbnailField, item, mediaUrl }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadThumbnail = async () => {
            try {
                const apiUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${thumbnailField}`;
                const headers = await apiHelper.getHeaders('graph/getMediaContent');
                const response = await fetch(apiUrl, { headers, mode: 'cors' });

                if (!response.ok) {
                    throw new Error(`Failed to fetch thumbnail: ${response.status}`);
                }

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                setThumbnailUrl(url);
            } catch (err) {

                setError(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadThumbnail();

        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailField]);

    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa'
            }}>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (error || !thumbnailUrl) {
        // Show gradient placeholder if thumbnail fails to load
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <FontAwesomeIcon icon={faMicrophone} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px' }}>Audio</span>
                {mediaUrl && (
                    <audio controls style={{ marginTop: '8px', width: '90%' }}>
                        <source src={mediaUrl} />
                    </audio>
                )}
            </div>
        );
    }

    return (
        <>
            <img 
                src={thumbnailUrl}
                alt={item.topic || item.Topic || 'Audio thumbnail'}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            {/* Audio overlay */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '24px',
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                opacity: 0.8,
                pointerEvents: 'none'
            }}>
                <FontAwesomeIcon icon={faMicrophone} />
            </div>
            {/* Audio controls at bottom if mediaUrl available */}
            {mediaUrl && (
                <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    right: '8px'
                }}>
                    <audio controls style={{ width: '100%', height: '24px' }}>
                        <source src={mediaUrl} />
                    </audio>
                </div>
            )}
        </>
    );
};

/** A Media Centre item's Wolfox briefing code, whichever casing the API returned. */
const briefingCodeOf = (item) =>
    (item?.briefingCode || item?.BriefingCode || '').trim();

// Media preview component for Media Centre items
const MediaPreview = React.memo(({ item }) => {
    const [mediaUrl, setMediaUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mediaType = (item.mediaType || item.MediaType || '').toLowerCase();
    const sourceUrl = item.mediaUrl || item.MediaUrl || item.webURLLink || item.WebURLLink;

    const isVideo = mediaType === 'video' || item.mimeType?.toLowerCase().startsWith('video/');
    const isAudio = mediaType === 'audio' || item.mimeType?.toLowerCase().startsWith('audio/');
    const isImage = mediaType === 'image' || item.mimeType?.toLowerCase().startsWith('image/');
    const isBriefing = mediaType === 'briefing';

    const loadMedia = async () => {
        if (!sourceUrl) {
            
            setError('No media URL available');
            return;
        }

        // Videos should NEVER load media content - just show placeholder
        if (isVideo) {
            setIsLoading(false);
            return;
        }
        
        // External audio platforms (Spotify, SoundCloud, etc.) should also not be loaded - just show placeholder
        if (isAudio && sourceUrl && (
            sourceUrl.includes('spotify.com') ||
            sourceUrl.includes('soundcloud.com') ||
            sourceUrl.includes('anchor.fm') ||
            sourceUrl.includes('pod.link')
        )) {
            
            setIsLoading(false);
            return;
        }

        
        setIsLoading(true);
        setError(null);
        
        try {
            // Check if it's a blob filename (no http/https) vs external URL
            const isBlobFile = !sourceUrl.startsWith('http');
            
            if (isBlobFile) {
                // For blob files, use efficient streaming URLs for audio
                if (isAudio) {
                    // Try streaming endpoint first (like member portal MediaCentre.js)
                    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '/');
                    const streamingUrl = `${baseUrl}media/stream/${sourceUrl}`;
                    
                    
                    try {
                        // Test if the streaming URL is accessible.
                        // No Range header: Range is not CORS-safelisted, so sending it
                        // turns this into a preflighted request, and the API's preflight
                        // does not allow Range - the fetch was rejected and we fell back
                        // to downloading the whole file. A bare HEAD is a simple request
                        // and needs no preflight. Same approach as the member portal's
                        // MediaCentre.
                        const testResponse = await fetch(streamingUrl, {
                            method: 'HEAD'
                        });
                        
                        if (testResponse.ok || testResponse.status === 206) {
                            
                            setMediaUrl(streamingUrl);
                        } else {
                            
                            throw new Error('Streaming not available');
                        }
                    } catch (streamError) {
                        
                        // Fallback to the graph API endpoint
                        const apiUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${sourceUrl}`;
                        const headers = await apiHelper.getHeaders('graph/getMediaContent');
                        const response = await fetch(apiUrl, { headers, mode: 'cors' });

                        if (!response.ok) {
                            throw new Error(`Failed to fetch from graph API: ${response.status}`);
                        }

                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        setMediaUrl(url);
                    }
                } else {
                    // For images, we still need to download the blob but it's much smaller
                    
                    const apiUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${sourceUrl}`;
                    const headers = await apiHelper.getHeaders('graph/getMediaContent');
                    const response = await fetch(apiUrl, { headers, mode: 'cors' });

                    if (!response.ok) {
                        throw new Error(`Failed to fetch image blob: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    
                    setMediaUrl(url);
                }
            } else {
                // This is an external URL - use directly for videos/audio, fetch as blob for images
                if (isVideo || isAudio) {
                                        
                    setMediaUrl(sourceUrl);
                } else {
                    // For external images, try to fetch as blob for better CORS handling
                    
                    const headers = await apiHelper.getHeaders('getMediaContent');
                    const response = await fetch(sourceUrl, { 
                        headers,
                        mode: 'cors'
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to fetch external image: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    setMediaUrl(url);
                }
            }
        } catch (err) {

            // Fallback: try using the URL directly

            setMediaUrl(sourceUrl);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (sourceUrl) {
            // For videos, NEVER load the actual media - just show placeholder
            if (isVideo) {
                setIsLoading(false);
                return;
            }
            
            // For external audio platforms, NEVER load the actual media - just show placeholder
            if (isAudio && (
                sourceUrl.includes('spotify.com') ||
                sourceUrl.includes('soundcloud.com') ||
                sourceUrl.includes('anchor.fm') ||
                sourceUrl.includes('pod.link')
            )) {
                
                setIsLoading(false);
                return;
            }
            
            // Check if it's a blob file or external URL
            const isBlobFile = !sourceUrl.startsWith('http');
            
            if (!isBlobFile && isAudio) {
                // External audio URLs (but not Spotify/SoundCloud/etc.) - use directly
                
                setMediaUrl(sourceUrl);
                setIsLoading(false);
            } else {
                // Blob audio files or images - need to fetch
                loadMedia();
            }
        } else if (!isBriefing) {
            setError('No media URL found');
        }

        return () => {
            if (mediaUrl && mediaUrl.startsWith('blob:')) {
                URL.revokeObjectURL(mediaUrl);
            }
        };
    }, [sourceUrl]);

    if (isBriefing) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #0f4c81 0%, #1b7ba0 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <FontAwesomeIcon icon={faHeadphones} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px', textAlign: 'center', padding: '0 8px' }}>
                    {briefingCodeOf(item) || 'Briefing'}
                </span>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa'
            }}>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (error && !mediaUrl) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: '#f8f9fa',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6c757d'
            }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px' }}>Error loading media</span>
            </div>
        );
    }

    if (isVideo) {
        // Check if video has a custom thumbnail
        const thumbnailField = item.thumbnail || item.Thumbnail || item.thumbnailUrl || item.ThumbnailUrl;
        const isExternalVideo = sourceUrl && (sourceUrl.includes('vimeo.com') || sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be'));
        
        // Priority 1: Use custom thumbnail if available (even for external videos)
        if (thumbnailField && thumbnailField.trim() !== '' && thumbnailField !== 'NONE') {
            return <VideoThumbnail thumbnailField={thumbnailField} item={item} />;
        }
        
        // Priority 2: For external videos without custom thumbnails, fetch platform thumbnails
        if (isExternalVideo) {
            return <ExternalVideoThumbnail sourceUrl={sourceUrl} item={item} />;
        }
        
        // Fallback: show placeholder for videos without thumbnails or external videos
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                position: 'relative'
            }}>
                <FontAwesomeIcon icon={faVideo} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px', textAlign: 'center', padding: '0 8px' }}>
                    {item.topic || item.Topic || 'Video'}
                </span>
               
            </div>
        );
    } else if (isImage && mediaUrl) {
        return (
            <img 
                src={mediaUrl}
                alt={item.topic || item.Topic || 'Image'} 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={() => setError('Failed to load image')}
            />
        );
    } else if (isAudio) {
        // Check if audio has a custom thumbnail
        const thumbnailField = item.thumbnail || item.Thumbnail || item.thumbnailUrl || item.ThumbnailUrl;
        
        if (thumbnailField) {
            return <AudioThumbnail thumbnailField={thumbnailField} item={item} mediaUrl={mediaUrl} />;
        }
        
        // Check if it's an external audio platform (Spotify, SoundCloud, etc.)
        const isExternalAudio = sourceUrl && (
            sourceUrl.includes('spotify.com') ||
            sourceUrl.includes('soundcloud.com') ||
            sourceUrl.includes('anchor.fm') ||
            sourceUrl.includes('pod.link')
        );
        
        if (isExternalAudio) {
            // Show platform-specific placeholder for external audio services
            const getPlatformInfo = (url) => {
                if (url.includes('spotify.com')) return { name: 'Spotify', icon: 'fab fa-spotify', color: '#1DB954' };
                if (url.includes('soundcloud.com')) return { name: 'SoundCloud', icon: 'fab fa-soundcloud', color: '#FF5500' };
                if (url.includes('anchor.fm')) return { name: 'Anchor', icon: 'fas fa-anchor', color: '#5000FF' };
                if (url.includes('pod.link')) return { name: 'Podcast', icon: 'fas fa-podcast', color: '#6366f1' };
                if (url.includes('goloudplayer.com')) return { name: 'GoLoud', icon: 'fas fa-volume-up', color: '#FF6B35' };
                return { name: 'Audio', icon: 'fas fa-headphones', color: '#4ade80' };
            };
            
            const platform = getPlatformInfo(sourceUrl);
            
            return (
                <div style={{
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(135deg, ${platform.color}20 0%, ${platform.color}40 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: platform.color,
                    border: `2px solid ${platform.color}30`
                }}>
                    <i className={platform.icon} style={{ fontSize: '32px', marginBottom: '8px' }}></i>
                    <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{platform.name}</span>
                    <span style={{ fontSize: '10px', opacity: 0.8, textAlign: 'center', padding: '0 8px', marginTop: '4px' }}>
                        {item.topic || item.Topic || 'Audio'}
                    </span>
                </div>
            );
        }
        
        // Fallback: show gradient background for local audio without thumbnails
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
            }}>
                <FontAwesomeIcon icon={faMicrophone} style={{ fontSize: '32px', marginBottom: '8px' }} />
                <span style={{ fontSize: '12px' }}>Audio</span>
                {mediaUrl && !sourceUrl.includes('spotify.com') && !sourceUrl.includes('soundcloud.com') && (
                    <audio controls style={{ marginTop: '8px', width: '90%' }}>
                        <source src={mediaUrl} />
                    </audio>
                )}
            </div>
        );
    }

    // Default fallback
    return (
        <div style={{
            width: '100%',
            height: '100%',
            background: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6c757d'
        }}>
            <FontAwesomeIcon icon={
                mediaType === 'video' ? faVideo : 
                mediaType === 'audio' ? faMicrophone : 
                faImage
            } style={{ fontSize: '32px', marginBottom: '8px' }} />
            <span style={{ fontSize: '12px' }}>{mediaType || 'Media'}</span>
        </div>
    );
});

const MediaCentreManagement = ({ searchTerm, isLoading: parentLoading, onItemCountChange }) => {
    // Media Centre items state
    const [mediaCentreItems, setMediaCentreItems] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    // Choice fields from SharePoint
    const [choiceFields, setChoiceFields] = useState({});
    const [isLoadingChoiceFields, setIsLoadingChoiceFields] = useState(false);

    // Filter and search state
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [selectedMediaType, setSelectedMediaType] = useState('all');
    const [selectedSeries, setSelectedSeries] = useState('all');
    const [selectedTheme, setSelectedTheme] = useState('all');
    const [selectedEntity, setSelectedEntity] = useState('all');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);

    // Modal and editing state
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddMediaModal, setShowAddMediaModal] = useState(false);
    
    // Enhanced gallery browser state
    const [showFullScreenGalleryBrowser, setShowFullScreenGalleryBrowser] = useState(false);
    const [selectedGalleryItems, setSelectedGalleryItems] = useState([]);
    const [showBulkPublishModal, setShowBulkPublishModal] = useState(false);

    // Selection state for bulk operations
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [isSelectMode, setIsSelectMode] = useState(false);

    // Gallery state for publishing workflow
    const [galleries, setGalleries] = useState([]);
    
    // Audio context for playing audio files
    const { playAudio } = useAudio();

    // Video and audio playback state (like MediaCentre.js)
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    // Briefing preview modal (Wolfox widget)
    const [selectedBriefing, setSelectedBriefing] = useState(null);
    const [showBriefingModal, setShowBriefingModal] = useState(false);
    
    // Video loading states (similar to MediaCentre.js)
    const [videoSource, setVideoSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    // Video loading logic (adapted from MediaCentre.js)
    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !selectedVideo?.video) return;

            loadingRef.current = true;
            setIsVideoLoading(true);
            setVideoSource(null);
            
            const video = selectedVideo.video;

            
            if (video.includes('https://')) {
                // Check if it's a YouTube or Vimeo URL that needs iframe embedding
                const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
                const isVimeo = video.includes('vimeo.com');

                if (isYouTube || isVimeo) {
                    // For YouTube/Vimeo, we'll set a flag to use iframe instead of video element
                    setVideoSource(getEmbeddableVideoUrl(video));
                    setIsVideoEmbedded(true);
                } else {
                    // For other HTTPS URLs, use them directly
                    setVideoSource(video);
                    setIsVideoEmbedded(false);
                }

                setIsVideoLoading(false);
                loadingRef.current = false;
            } else {
                // For local videos, try different streaming endpoint patterns for admin centre

                
                // Try multiple possible streaming URL patterns
                const possibleUrls = [
                    // Pattern 1: Like member portal (direct media stream)
                    `${process.env.REACT_APP_API_URL.replace(/\/api$/, '')}/media/stream/${video}`,
                    // Pattern 2: Through API endpoint 
                    `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${video}`,
                    // Pattern 3: Direct API media stream
                    `${process.env.REACT_APP_API_URL}/media/stream/${video}`,
                    // Pattern 4: API media endpoint
                    `${process.env.REACT_APP_API_URL}/api/media/stream/${video}`
                ];


                
                let videoUrlFound = false;
                
                for (const streamingUrl of possibleUrls) {
                    try {
                        // Only the /api/graph/ endpoint is behind Auth0; the /media/stream
                        // mounts are unauthenticated. Sending Authorization to those anyway
                        // would force a CORS preflight for no reason, so probe them bare -
                        // the same way the member portal's MediaCentre does.
                        //
                        // No Range header either: Range is not CORS-safelisted, so sending
                        // it turns this into a preflighted request that the API's preflight
                        // rejects, which is what made this check "fail".
                        const needsAuth = streamingUrl.includes('/api/graph/');
                        const testResponse = await fetch(streamingUrl, {
                            method: 'HEAD',
                            ...(needsAuth ? { headers: await apiHelper.getHeaders() } : {})
                        });
                        

                        
                        if (testResponse.ok || testResponse.status === 206) {

                            setVideoSource(streamingUrl);
                            setIsVideoEmbedded(false);
                            videoUrlFound = true;
                            break;
                        } else {

                        }
                    } catch (error) {

                        continue;
                    }
                }
                
                if (!videoUrlFound) {

            
                    // Show a user-friendly error message
                    setVideoSource(null);
                    setIsVideoEmbedded(false);
                }
                
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        if (selectedVideo?.video) {
            loadVideo();
        }

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [selectedVideo?.video]);

    // Helper functions from MediaCentre.js
    const getEmbeddableVideoUrl = (url) => {
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url; // Return original URL if not YouTube/Vimeo
    };

    const getVimeoEmbedUrl = (vimeoUrl) => {
        // Match both regular and private video URLs with privacy hash
        const match = vimeoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
        if (!match) return null;
        
        const videoId = match[1];
        const privacyHash = match[2]; // Will be undefined for public videos
        
        // Build embed URL with privacy hash as parameter if present
        let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
        
        if (privacyHash) {
            embedUrl += `&h=${privacyHash}`;
        }
        
        return embedUrl;
    };

    // Fetch media centre items
    const fetchMediaCentreItems = useCallback(async () => {
        setIsLoadingItems(true);
        try {
            const items = await getMediaCentreItems();

            
            setMediaCentreItems(items);
            setLastRefresh(new Date());
        } catch (error) {
            toast.error('Failed to load Media Centre items');
        } finally {
            setIsLoadingItems(false);
        }
    }, []);

    // Fetch SharePoint choice fields
    const fetchChoiceFields = useCallback(async () => {
        setIsLoadingChoiceFields(true);
        try {
            const fields = await getChoiceFieldOptions();
            setChoiceFields(fields);
        } catch (error) {
            toast.warning('Some filter options may not be available');
        } finally {
            setIsLoadingChoiceFields(false);
        }
    }, []);

    // Fetch galleries for publishing workflow
    const fetchGalleries = useCallback(async () => {
        try {
            const fetchedGalleries = await galleryService.getGalleries();
            setGalleries(fetchedGalleries);
        } catch (error) {
            // Silent fail for galleries
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        fetchMediaCentreItems();
        fetchChoiceFields();
        fetchGalleries();
    }, [fetchMediaCentreItems, fetchChoiceFields, fetchGalleries]);

    // Notify parent of item count changes (should be total, not filtered)
    useEffect(() => {
        if (onItemCountChange) {
            onItemCountChange(mediaCentreItems.length);
        }
    }, [mediaCentreItems.length, onItemCountChange]);

    // Filter and search logic
    const filteredItems = useMemo(() => {
        const searchQuery = (searchTerm || localSearchTerm).toLowerCase().trim();
        
        return mediaCentreItems.filter(item => {
            // Search filter
            const matchesSearch = !searchQuery || 
                item.topic?.toLowerCase().includes(searchQuery) ||
                (item.ShortDescriptionOfContent || item.shortDescription)?.toLowerCase().includes(searchQuery) ||
                item.entity?.toLowerCase().includes(searchQuery);

            // Media type filter
            const matchesMediaType = selectedMediaType === 'all' || 
                item.mediaType?.toLowerCase() === selectedMediaType.toLowerCase();

            // Series filter
            const matchesSeries = selectedSeries === 'all' || 
                (Array.isArray(item.series) ? item.series.includes(selectedSeries) : item.series === selectedSeries);

            // Theme filter
            const matchesTheme = selectedTheme === 'all' || item.theme === selectedTheme;

            // Entity filter
            const matchesEntity = selectedEntity === 'all' || item.entity === selectedEntity;

            return matchesSearch && matchesMediaType && matchesSeries && matchesTheme && matchesEntity;
        }).sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate)); // Newest first
    }, [mediaCentreItems, searchTerm, localSearchTerm, selectedMediaType, selectedSeries, selectedTheme, selectedEntity]);

    // Memoized counts to avoid recalculation
    const counts = useMemo(() => ({
        total: mediaCentreItems.length,
        filtered: filteredItems.length,
        hasActiveFilters: selectedMediaType !== 'all' || selectedSeries !== 'all' || selectedTheme !== 'all' || selectedEntity !== 'all' || (localSearchTerm?.trim().length > 0)
    }), [mediaCentreItems.length, filteredItems.length, selectedMediaType, selectedSeries, selectedTheme, selectedEntity, localSearchTerm]);

    // Pagination logic
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Get unique values for filters
    const filterOptions = useMemo(() => {
        return {
            mediaTypes: [...new Set(mediaCentreItems.map(item => item.mediaType).filter(Boolean))],
            series: [...new Set(mediaCentreItems.flatMap(item => 
                Array.isArray(item.series) ? item.series : [item.series]
            ).filter(Boolean))],
            themes: choiceFields.themes || [...new Set(mediaCentreItems.map(item => item.theme).filter(Boolean))],
            entities: choiceFields.entities || [...new Set(mediaCentreItems.map(item => item.entity).filter(Boolean))]
        };
    }, [mediaCentreItems, choiceFields]);

    // Handle refresh
    // Media click handlers (like MediaCentre.js)
    const handleMediaClick = async (mediaItem, event) => {

        // Handle audio briefings — previewed via the embedded Wolfox player, so
        // there is no media file to load or stream.
        if ((mediaItem.mediaType || mediaItem.MediaType || '').toLowerCase() === 'briefing') {
            setSelectedBriefing(mediaItem);
            setShowBriefingModal(true);
            return;
        }

        // Handle image files
        if ((mediaItem.mediaType || mediaItem.MediaType || '').toLowerCase() === 'image') {
            handleImageClick(mediaItem, event);
            return;
        }
        
        // Handle audio files
        if ((mediaItem.mediaType || mediaItem.MediaType || '').toLowerCase() === 'audio') {

            
            if (!playAudio) {

                toast.error('Audio player is not available. Please refresh the page.');
                return;
            }
            
            try {
                // Capture cursor position for modal positioning
                let mousePosition = null;
                if (event) {
                    mousePosition = {
                        x: event.clientX,
                        y: event.clientY,
                        scrollY: window.scrollY
                    };

                }
                
                // Get the audio URL from various possible field names
                const audioUrl = mediaItem.mediaUrl || mediaItem.MediaUrl || mediaItem.webURLLink || mediaItem.WebURLLink;
                
                // Check if it's an external audio URL (Spotify, SoundCloud, etc.)
                if (audioUrl && (
                    audioUrl.includes('spotify.com') ||
                    audioUrl.includes('soundcloud.com') ||
                    audioUrl.includes('anchor.fm') ||
                    audioUrl.includes('pod.link') ||
                    audioUrl.includes('goloudplayer.com') ||
                    audioUrl.includes('https://') ||
                    audioUrl.includes('http://')
                )) {
                    // External URL - pass directly to AudioContext

                    
                    // Create media item with consistent field names
                    const audioMediaItem = {
                        ...mediaItem,
                        webURLLink: audioUrl,
                        topic: mediaItem.topic || mediaItem.Topic,
                        shortDescription: mediaItem.shortDescription || mediaItem.ShortDescription || mediaItem.ShortDescriptionOfContent,
                        publishDate: mediaItem.publishDate || mediaItem.PublishDate,
                        entity: mediaItem.entity || mediaItem.Entity,
                        duration: mediaItem.duration || mediaItem.Duration
                    };
                    
                    playAudio(audioMediaItem, mousePosition);

                    return;
                }
                
                // Local blob audio file - need to load it first

                
                // Extract filename from various URL patterns (similar to video handling)
                let audioFilename = audioUrl;
                
                // If it's a full URL, extract just the filename
                if (audioUrl && audioUrl.includes('/media/stream/')) {
                    audioFilename = audioUrl.split('/media/stream/')[1];
                } else if (audioUrl && audioUrl.includes('/')) {
                    // If it has slashes but not HTTP, take the last part
                    audioFilename = audioUrl.split('/').pop();
                }
                // If it's already just a filename (no slashes), use as-is
                

                
                // Create media item with loaded URL and consistent field names
                const audioMediaItem = {
                    ...mediaItem,
                    webURLLink: audioFilename, // AudioContext will handle the loading
                    topic: mediaItem.topic || mediaItem.Topic,
                    shortDescription: mediaItem.shortDescription || mediaItem.ShortDescription || mediaItem.ShortDescriptionOfContent,
                    publishDate: mediaItem.publishDate || mediaItem.PublishDate,
                    entity: mediaItem.entity || mediaItem.Entity,
                    duration: mediaItem.duration || mediaItem.Duration
                };
                
                playAudio(audioMediaItem, mousePosition);

                
            } catch (error) {

                toast.error('Error starting audio: ' + error.message);
            }
            return;
        }
        
        // Handle video files
        if ((mediaItem.mediaType || mediaItem.MediaType || '').toLowerCase() === 'video') {
            const videoUrl = mediaItem.mediaUrl || mediaItem.MediaUrl || mediaItem.webURLLink || mediaItem.WebURLLink;
            
            // Check if it's a Vimeo or YouTube URL
            if (videoUrl && (videoUrl.includes('vimeo.com') || 
                videoUrl.includes('youtube.com') || 
                videoUrl.includes('youtu.be'))) {
                
                // Create video item with webURLLink format (like MediaCentre.js)
                const videoMediaItem = {
                    ...mediaItem,
                    video: videoUrl,
                    webURLLink: videoUrl
                };
                
                setSelectedVideo(videoMediaItem);
                setShowVideoModal(true);
                return;
            }
            
            // Handle local video files (from blob storage)

            
            // Extract filename from various URL patterns
            let videoFilename = videoUrl;
            
            if (videoUrl && videoUrl.includes('/media/stream/')) {
                videoFilename = videoUrl.split('/media/stream/')[1];
            } else if (videoUrl && (videoUrl.includes('http://') || videoUrl.includes('https://'))) {
                videoFilename = videoUrl.split('/').pop();
            } else if (videoUrl && videoUrl.includes('/')) {
                videoFilename = videoUrl.split('/').pop();
            }
            

            
            // Create a modified mediaItem with the correct video reference
            const videoMediaItem = {
                ...mediaItem,
                video: videoFilename,
                topic: mediaItem.topic || mediaItem.Topic,
                publishDate: mediaItem.publishDate || mediaItem.PublishDate,
                entity: mediaItem.entity || mediaItem.Entity,
                duration: mediaItem.duration || mediaItem.Duration
            };
            
            setSelectedVideo(videoMediaItem);
            setShowVideoModal(true);
            return;
        }
        
        // Default fallback
        const mediaUrl = mediaItem.mediaUrl || mediaItem.MediaUrl || mediaItem.webURLLink || mediaItem.WebURLLink;
        if (mediaUrl) {
            window.open(mediaUrl, '_blank');
        }
    };

    const handleImageClick = (mediaItem, event) => {

        
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        // Get the already-loaded image URL from the thumbnail
        const thumbnailImg = event?.currentTarget?.querySelector('img');
        const loadedImageUrl = thumbnailImg?.src;
        
                
        
        // Pass the already-loaded image URL to the modal
        setSelectedImage({
            ...mediaItem,
            loadedImageUrl: loadedImageUrl // Add the pre-loaded URL
        });
        setShowImageModal(true);
    };

    // Close video modal
    const closeVideoModal = () => {
        setShowVideoModal(false);
        setSelectedVideo(null);
        
        // Clean up video state
        setVideoSource(null);
        setIsVideoLoading(true);
        setIsVideoEmbedded(false);
        
        // Clean up blob URLs
        if (currentBlobUrl.current) {
            URL.revokeObjectURL(currentBlobUrl.current);
            currentBlobUrl.current = null;
        }
        
        loadingRef.current = false;
    };

    // Toggle fullscreen for video modal
    const toggleVideoFullscreen = () => {
        const videoModal = document.querySelector('.video-modal');
        
        if (!document.fullscreenElement) {
            // Enter fullscreen
            if (videoModal.requestFullscreen) {
                videoModal.requestFullscreen();
            } else if (videoModal.webkitRequestFullscreen) { // Safari
                videoModal.webkitRequestFullscreen();
            } else if (videoModal.msRequestFullscreen) { // IE/Edge
                videoModal.msRequestFullscreen();
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { // Safari
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { // IE/Edge
                document.msExitFullscreen();
            }
        }
    };

    // ESC key handler for modal closing
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (showImageModal) {
                    setShowImageModal(false);
                }
                if (showVideoModal) {
                    closeVideoModal();
                }
            }
        };

        // Add event listener when modals are open
        if (showImageModal || showVideoModal) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showImageModal, showVideoModal]);

    const handleRefresh = async () => {
        await Promise.all([
            fetchMediaCentreItems(),
            fetchChoiceFields(),
            fetchGalleries()
        ]);
       
    };



    // Handle successful publish
    const handlePublishSuccess = () => {
        setShowPublishModal(false);
        setSelectedGalleryItem(null);
        fetchMediaCentreItems(); // Refresh the list
        fetchChoiceFields(); // Refresh choice fields to include any new custom values
        toast.success('Item published to Media Centre successfully! 🎉');
    };

    // Handle manual media add
    const handleAddMedia = () => {
        setShowAddMediaModal(true);
    };

    // Handle successful manual add
    const handleAddMediaSuccess = () => {
        setShowAddMediaModal(false);
        fetchMediaCentreItems(); // Refresh the list
        fetchChoiceFields(); // Refresh choice fields to include any new custom values
        toast.success('Media added to Media Centre successfully! 🎉');
    };

    // Handle enhanced gallery browser
    const handleOpenFullScreenGalleryBrowser = () => {
        setShowFullScreenGalleryBrowser(true);
    };

    // Handle bulk gallery item selection
    const handleBulkGalleryItemSelect = (items) => {
        setSelectedGalleryItems(items);
        setShowFullScreenGalleryBrowser(false);
        if (items.length > 0) {
            setShowBulkPublishModal(true);
        }
    };

    // Handle bulk publish success
    const handleBulkPublishSuccess = () => {
        setShowBulkPublishModal(false);
        setSelectedGalleryItems([]);
        fetchMediaCentreItems(); // Refresh the list
        fetchChoiceFields(); // Refresh choice fields to include any new custom values
        toast.success(`${selectedGalleryItems.length} items published to Media Centre successfully! 🎉`);
    };

    // Handle edit item
    const handleEditItem = (item) => {
        setEditingItem(item);
        setShowEditModal(true);
    };

    // Handle edit success
    const handleEditSuccess = () => {
        setShowEditModal(false);
        setEditingItem(null);
        fetchMediaCentreItems(); // Refresh the list
        fetchChoiceFields(); // Refresh choice fields to include any new custom values
        // Note: Success message is already shown by the modal component
    };

    // Handle delete item
    const handleDeleteItem = async (item) => {
        if (window.confirm(`Are you sure you want to delete "${item.topic || item.Topic}"?\n\nThis action cannot be undone.`)) {
            try {
                const itemId = item.id;
            
                
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/graph/media-center/items/${itemId}`, {
                    method: 'DELETE',
                    headers: await apiHelper.getHeaders()
                });
                
                if (response.ok) {
                    const result = await response.json();
              
                    
                    toast.success(`"${item.topic || item.Topic}" deleted successfully`);
                    
                    // Refresh the media centre items
                    await handleRefresh();
                } else {
                    const errorData = await response.json();

                    toast.error(`Failed to delete item: ${errorData.details || 'Unknown error'}`);
                }
            } catch (error) {

                toast.error(`Failed to delete item: ${error.message}`);
            }
        }
    };

    // Handle bulk operations
    const handleSelectAll = () => {
        if (selectedItems.size === paginatedItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(paginatedItems.map(item => item.id)));
        }
    };

    const handleItemSelect = (itemId) => {
        const newSelected = new Set(selectedItems);
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId);
        } else {
            newSelected.add(itemId);
        }
        setSelectedItems(newSelected);
    };

    // Get media type icon
    const getMediaTypeIcon = (mediaType) => {
        switch (mediaType?.toLowerCase()) {
            case 'video': return faVideo;
            case 'audio': return faMicrophone;
            case 'image': return faImage;
            case 'briefing': return faHeadphones;
            default: return faBroadcastTower;
        }
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };



    return (
        <div className="media-centre-management">
            {/* Header */}
            <div className="management-header">
                <div className="header-info">
                    <h3>
                        <FontAwesomeIcon icon={faBroadcastTower} />
                        Media Centre Management
                    </h3>
                    <p className="text-muted">
                        Manage published content and publish from galleries
                    </p>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-number">{counts.total}</span>
                        <span className="stat-label">Total Items</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-number">{counts.filtered}</span>
                        <span className="stat-label">{counts.hasActiveFilters ? 'Filtered' : 'Total'}</span>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="management-toolbar">
                <div className="toolbar-left">
                    <div className="search-box">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Search media centre items..."
                            value={localSearchTerm}
                            onChange={(e) => setLocalSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <Tippy content={showFilters ? "Hide Filters" : "Show Filters"}>
                        <button 
                            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            Filters
                        </button>
                    </Tippy>
                </div>

                <div className="toolbar-right">
                    <div className="publish-buttons">
                        <Tippy content="Add External Media (Vimeo, YouTube, URLs)">
                            <button 
                                className="publish-btn add-media"
                                onClick={handleAddMedia}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        </Tippy>
                    </div>

                    <Tippy content="Browse All Galleries (Bulk Upload)">
                        <button
                            className="browse-galleries-btn"
                            onClick={handleOpenFullScreenGalleryBrowser}
                        >
                            <FontAwesomeIcon icon={faCloudArrowUp} className="me-1" />
                            Bulk upload from Galleries
                        </button>
                    </Tippy>

                    <button
                        className={`refresh-btn ${isLoadingItems ? 'loading' : ''}`}
                        onClick={handleRefresh}
                        disabled={isLoadingItems}
                    >
                        <FontAwesomeIcon 
                            icon={faRotate} 
                            className={isLoadingItems ? 'fa-spin' : ''} 
                        />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="filters-section">
                    <div className="filters-row">
                        <div className="filter-group">
                            <label>Media Type</label>
                            <select 
                                value={selectedMediaType} 
                                onChange={(e) => setSelectedMediaType(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Types</option>
                                {filterOptions.mediaTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Series</label>
                            <select 
                                value={selectedSeries} 
                                onChange={(e) => setSelectedSeries(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Series</option>
                                {filterOptions.series.map(series => (
                                    <option key={series} value={series}>{series}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Theme</label>
                            <select 
                                value={selectedTheme} 
                                onChange={(e) => setSelectedTheme(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Themes</option>
                                {filterOptions.themes.map(theme => (
                                    <option key={theme} value={theme}>{theme}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Entity</label>
                            <select 
                                value={selectedEntity} 
                                onChange={(e) => setSelectedEntity(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Entities</option>
                                {filterOptions.entities.map(entity => (
                                    <option key={entity} value={entity}>{entity}</option>
                                ))}
                            </select>
                        </div>

                        {counts.hasActiveFilters && (
                            <div className="filter-actions">
                                <button 
                                    className="clear-filters-btn"
                                    onClick={() => {
                                        setSelectedMediaType('all');
                                        setSelectedSeries('all');
                                        setSelectedTheme('all');
                                        setSelectedEntity('all');
                                        setLocalSearchTerm('');
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>

                    {counts.hasActiveFilters && (
                        <div className="filter-summary">
                            <div className="active-filters">
                                <span className="active-filters-label">Active filters:</span>
                                {selectedMediaType !== 'all' && (
                                    <span className="filter-tag">
                                        Type: {selectedMediaType}
                                        <button onClick={() => setSelectedMediaType('all')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                )}
                                {selectedSeries !== 'all' && (
                                    <span className="filter-tag">
                                        Series: {selectedSeries}
                                        <button onClick={() => setSelectedSeries('all')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                )}
                                {selectedTheme !== 'all' && (
                                    <span className="filter-tag">
                                        Theme: {selectedTheme}
                                        <button onClick={() => setSelectedTheme('all')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                )}
                                {selectedEntity !== 'all' && (
                                    <span className="filter-tag">
                                        Entity: {selectedEntity}
                                        <button onClick={() => setSelectedEntity('all')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                )}
                                {localSearchTerm && (
                                    <span className="filter-tag">
                                        Search: "{localSearchTerm}"
                                        <button onClick={() => setLocalSearchTerm('')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="content-area">
                {isLoadingItems ? (
                    <div className="loading-container">
                        <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
                        <p>Loading Media Centre items...</p>
                    </div>
                ) : counts.filtered === 0 ? (
                    <div className="empty-state">
                        <FontAwesomeIcon icon={faBroadcastTower} className="empty-icon" />
                        <h3>{counts.total === 0 ? 'No Media Centre Items Found' : 'No Items Match Your Filters'}</h3>
                        <p>{counts.total === 0 ? 'Start by publishing content from your galleries!' : 'Try adjusting your search or filter criteria.'}</p>
                        {counts.total === 0 ? (
                            <button 
                                className="btn-primary"
                                onClick={handleOpenFullScreenGalleryBrowser}
                            >
                                <FontAwesomeIcon icon={faCloudArrowUp} />
                                Browse Galleries
                            </button>
                        ) : (
                            <button 
                                className="btn-secondary"
                                onClick={() => {
                                    setSelectedMediaType('all');
                                    setSelectedSeries('all');
                                    setSelectedTheme('all');
                                    setSelectedEntity('all');
                                    setLocalSearchTerm('');
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                Clear All Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="items-grid">
                        {filteredItems.map((item) => {

                            return (
                            <div 
                                key={item.id || item.Id} 
                                className="media-centre-item-card"
                                onClick={(e) => handleMediaClick(item, e)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="item-thumbnail">
                                    <div className="media-type-indicator">
                                        <FontAwesomeIcon icon={getMediaTypeIcon(item.mediaType || item.MediaType)} />
                                    </div>
                                    <MediaPreview item={item} />
                                </div>

                                <div className="item-info">
                                    <h4 className="item-title">
                                        {item.topic || item.Topic || item.title || item.Title || item.topicName || item.TopicName || 'Untitled'}
                                    </h4>
                                                                                                              <p className="item-description">
                                        {item.ShortDescriptionOfContent || item.shortDescription || item.ShortDescription || item.description || item.Description || item.Brief || item.Summary || 'No description available'}
                                    </p>

                                    <div className="item-metadata">
                                        <span className="meta-date">
                                            <FontAwesomeIcon icon={faCalendarDays} />
                                            {formatDate(item.publishDate || item.PublishDate || item.Created)}
                                        </span>
                                        <span className="meta-entity">
                                            <FontAwesomeIcon icon={faBuilding} />
                                            {item.entity || item.Entity || 'Unknown'}
                                        </span>
                                        <span className={`meta-published ${(item.published || item.Published) ? 'published' : 'unpublished'}`}>
                                            <FontAwesomeIcon icon={(item.published || item.Published) ? faCheckCircle : faTimesCircle} />
                                            {(item.published || item.Published) ? 'Published' : 'Unpublished'}
                                        </span>
                                    </div>

                                    <div className="item-tags">
                                        {((item.series || item.Series) && Array.isArray(item.series || item.Series)) ? 
                                            (item.series || item.Series).map(series => (
                                                <span key={series} className="tag series-tag">{series}</span>
                                            )) : (item.series || item.Series) && (
                                                <span className="tag series-tag">{item.series || item.Series}</span>
                                            )
                                        }
                                        {(item.theme || item.Theme) && (
                                            <span className="tag theme-tag">{item.theme || item.Theme}</span>
                                        )}
                                        {(item.productService || item.ProductService || item.product || item.Product) && (
                                            <span className="tag product-tag">{item.productService || item.ProductService || item.product || item.Product}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="item-actions">
                                    <Tippy content="Edit Item">
                                        <button 
                                            className="action-btn edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditItem(item);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                    </Tippy>
                                    <Tippy content="Delete Item">
                                        <button 
                                            className="action-btn delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteItem(item);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </Tippy>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showPublishModal && selectedGalleryItem && (
                <ModalSendToMediaCentre
                    isOpen={showPublishModal}
                    onClose={() => {
                        setShowPublishModal(false);
                        setSelectedGalleryItem(null);
                    }}
                    onSuccess={handlePublishSuccess}
                    selectedItem={selectedGalleryItem}
                    choiceFields={choiceFields}
                />
            )}

            <ModalAddMediaCentre
                isOpen={showAddMediaModal}
                onClose={() => setShowAddMediaModal(false)}
                onSuccess={handleAddMediaSuccess}
                choiceFields={choiceFields}
            />

            <EnhancedGalleryBrowser
                isOpen={showFullScreenGalleryBrowser}
                onClose={() => setShowFullScreenGalleryBrowser(false)}
                onSelectItems={handleBulkGalleryItemSelect}
                galleries={galleries}
            />

            <ModalBulkPublishToMediaCentre
                isOpen={showBulkPublishModal}
                onClose={() => {
                    setShowBulkPublishModal(false);
                    setSelectedGalleryItems([]);
                }}
                onSuccess={handleBulkPublishSuccess}
                selectedItems={selectedGalleryItems}
                choiceFields={choiceFields}
            />

            {showEditModal && editingItem && (
                <ModalAddMediaCentre
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingItem(null);
                    }}
                    onSuccess={handleEditSuccess}
                    choiceFields={choiceFields}
                    editingItem={editingItem}
                    isEditMode={true}
                />
            )}

            {/* Video Modal (like MediaCentre.js) */}
            {showVideoModal && selectedVideo && (
                <div className="video-modal-overlay" onClick={closeVideoModal}>
                    <div className="video-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="video-modal-header">
                            <h3>{selectedVideo.topic || selectedVideo.title}</h3>
                            <div className="video-modal-controls">
                                <button className="fullscreen-btn" onClick={toggleVideoFullscreen} title="Toggle Fullscreen">
                                    <FontAwesomeIcon icon={faExpand} />
                                </button>
                                <button className="close-btn" onClick={closeVideoModal}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                        <div className="video-modal-content">
                            {/* Handle Vimeo/YouTube videos for external URLs */}
                            {selectedVideo.webURLLink && (
                                selectedVideo.webURLLink.includes('vimeo.com') || 
                                selectedVideo.webURLLink.includes('youtube.com') || 
                                selectedVideo.webURLLink.includes('youtu.be')
                            ) ? (
                                <iframe
                                    src={selectedVideo.webURLLink.includes('vimeo.com') 
                                        ? getVimeoEmbedUrl(selectedVideo.webURLLink)
                                        : getEmbeddableVideoUrl(selectedVideo.webURLLink)
                                    }
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    title={selectedVideo.topic || selectedVideo.title}
                                ></iframe>
                            ) : (
                                /* Handle local blob videos with proper loading */
                                <>
                                    {isVideoLoading ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '48px' }} />
                                        </div>
                                    ) : videoSource ? (
                                        isVideoEmbedded ? (
                                            <iframe
                                                src={videoSource}
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                title={selectedVideo.topic || selectedVideo.title}
                                            />
                                        ) : (
                                            <video
                                                width="100%"
                                                height="100%"
                                                controls
                                                autoPlay
                                                src={videoSource}
                                                onError={(e) => {
    
                                                }}
        
                                            >
                                                Your browser does not support the video tag.
                                            </video>
                                        )
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#666' }}>
                                            <p>Failed to load video</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="video-modal-info">
                                                         <div className="video-modal-meta">
                                 {selectedVideo.duration && (
                                     <span><FontAwesomeIcon icon={faClock} /> {selectedVideo.duration}</span>
                                 )}
                                <span><FontAwesomeIcon icon={faCalendarDays} /> {formatDate(selectedVideo.publishDate || selectedVideo.PublishDate)}</span>
                                <span><FontAwesomeIcon icon={faBuilding} /> {selectedVideo.entity || selectedVideo.Entity}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal (like MediaCentre.js) */}
            {showImageModal && selectedImage && (
                <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
                    <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="image-modal-header">
                            <h3>
                                <span>{(selectedImage.topic || selectedImage.Topic || 'Image').length > 50 ? 
                                    `${(selectedImage.topic || selectedImage.Topic || 'Image').substring(0, 50)}...` : 
                                    (selectedImage.topic || selectedImage.Topic || 'Image')}</span>
                            </h3>
                            <button 
                                className="image-modal-close"
                                onClick={() => setShowImageModal(false)}
                                title="Close image"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="image-modal-content">
                            <img 
                                src={selectedImage.loadedImageUrl || selectedImage.mediaUrl || selectedImage.MediaUrl || selectedImage.webURLLink || selectedImage.WebURLLink} 
                                alt={selectedImage.topic || selectedImage.Topic || 'Image'} 
                            />
                        </div>
                        <div className="image-modal-info">
                            <div className="image-modal-meta">
                                <span><FontAwesomeIcon icon={faCalendarDays} /> {formatDate(selectedImage.publishDate || selectedImage.PublishDate)}</span>
                                <span><FontAwesomeIcon icon={faBuilding} /> {selectedImage.entity || selectedImage.Entity}</span>
                                {(selectedImage.ShortDescriptionOfContent || selectedImage.shortDescription) && (
                                    <div className="image-description">
                                        <p>{selectedImage.ShortDescriptionOfContent || selectedImage.shortDescription}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Briefing Modal — previews the Wolfox audio player for a Briefing item */}
            {showBriefingModal && selectedBriefing && (
                <div className="image-modal-overlay" onClick={() => setShowBriefingModal(false)}>
                    <div
                        className="image-modal-container briefing-modal-container"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="image-modal-header">
                            <h3>
                                <span>{selectedBriefing.topic || selectedBriefing.Topic || 'Briefing'}</span>
                            </h3>
                            <button
                                className="image-modal-close"
                                onClick={() => setShowBriefingModal(false)}
                                title="Close briefing"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="image-modal-content briefing-modal-content">
                            {briefingCodeOf(selectedBriefing) ? (
                                <WolfoxWidget
                                    type="daily-policy"
                                    briefing={briefingCodeOf(selectedBriefing)}
                                />
                            ) : (
                                <div style={{ padding: '16px', color: '#6c757d' }}>
                                    <p style={{ marginBottom: '4px', fontWeight: 600 }}>
                                        This item has no briefing code.
                                    </p>
                                    <p style={{ margin: 0, fontSize: '13px' }}>
                                        Edit the item and enter a briefing code (for example
                                        "latest") to preview the player.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="image-modal-info">
                            <div className="image-modal-meta">
                                <span><FontAwesomeIcon icon={faCalendarDays} /> {formatDate(selectedBriefing.publishDate || selectedBriefing.PublishDate)}</span>
                                <span><FontAwesomeIcon icon={faBuilding} /> {selectedBriefing.entity || selectedBriefing.Entity}</span>
                            </div>
                            {(selectedBriefing.ShortDescriptionOfContent || selectedBriefing.shortDescription) && (
                                <div className="image-description">
                                    <p>{selectedBriefing.ShortDescriptionOfContent || selectedBriefing.shortDescription}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaCentreManagement; 